import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
  useRef,
} from 'react';
import { io, Socket } from 'socket.io-client';
import { AUTH_TOKEN_CHANGED_EVENT } from '../constants/authEvents';

interface WebSocketContextType {
    socket: Socket | null;
    connected: boolean;
    emit: (event: string, data?: any) => void;
    on: (event: string, callback: (...args: any[]) => void) => void;
    off: (event: string, callback?: (...args: any[]) => void) => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export const useWebSocket = () => {
    const context = useContext(WebSocketContext);
    if (!context) {
        throw new Error('useWebSocket must be used within a WebSocketProvider');
    }
    return context;
};

interface WebSocketProviderProps {
    children: ReactNode;
}

const readAuthToken = (): string | null => {
    if (typeof window === 'undefined') {
        return null;
    }
    try {
        return localStorage.getItem('token');
    } catch {
        return null;
    }
};

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
    const [socket, setSocket] = useState<Socket | null>(null);
    const [connected, setConnected] = useState(false);
    const [authToken, setAuthToken] = useState<string | null>(readAuthToken);
    const listenersRef = useRef<Map<string, Set<(...args: any[]) => void>>>(new Map());

    const syncAuthToken = useCallback(() => {
        setAuthToken(readAuthToken());
    }, []);

    const attachStoredListeners = useCallback((activeSocket: Socket) => {
        listenersRef.current.forEach((callbacks, event) => {
            callbacks.forEach((callback) => {
                activeSocket.on(event, callback);
            });
        });
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }
        window.addEventListener('storage', syncAuthToken);
        window.addEventListener(AUTH_TOKEN_CHANGED_EVENT, syncAuthToken);
        return () => {
            window.removeEventListener('storage', syncAuthToken);
            window.removeEventListener(AUTH_TOKEN_CHANGED_EVENT, syncAuthToken);
        };
    }, [syncAuthToken]);

    useEffect(() => {
        if (process.env.NODE_ENV === 'test') {
            setSocket(null);
            setConnected(false);
            return undefined;
        }

        const apiUrlRaw = process.env.REACT_APP_WS_URL
            || process.env.REACT_APP_API_URL
            || 'https://carbonintelligence.sustainow.in';
        // DEEP-BUG-006: never open ws:// from an HTTPS page (mixed content).
        let apiUrl = apiUrlRaw;
        if (typeof window !== 'undefined' && window.location?.protocol === 'https:') {
            apiUrl = String(apiUrlRaw).replace(/^ws:\/\//i, 'wss://').replace(/^http:\/\//i, 'https://');
        } else if (typeof window !== 'undefined' && window.location?.protocol === 'http:') {
            apiUrl = String(apiUrlRaw).replace(/^wss:\/\//i, 'ws://').replace(/^https:\/\//i, 'http://');
        }

        if (!authToken) {
            setSocket(null);
            setConnected(false);
            return undefined;
        }

        const newSocket = io(apiUrl, {
            auth: {
                token: authToken
            },
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionAttempts: 5
        });

        const onConnect = () => setConnected(true);
        const onDisconnect = () => setConnected(false);
        const onConnectError = () => setConnected(false);

        newSocket.on('connect', onConnect);
        newSocket.on('disconnect', onDisconnect);
        newSocket.on('connect_error', onConnectError);
        attachStoredListeners(newSocket);

        setSocket(newSocket);

        return () => {
            newSocket.off('connect', onConnect);
            newSocket.off('disconnect', onDisconnect);
            newSocket.off('connect_error', onConnectError);
            newSocket.disconnect();
        };
    }, [authToken, attachStoredListeners]);

    const emit = useCallback((event: string, data?: any) => {
        if (socket && connected) {
            socket.emit(event, data);
        }
    }, [socket, connected]);

    const on = useCallback((event: string, callback: (...args: any[]) => void) => {
        const existing = listenersRef.current.get(event) || new Set();
        existing.add(callback);
        listenersRef.current.set(event, existing);
        if (socket) {
            socket.on(event, callback);
        }
    }, [socket]);

    const off = useCallback((event: string, callback?: (...args: any[]) => void) => {
        const existing = listenersRef.current.get(event);
        if (existing) {
            if (callback) {
                existing.delete(callback);
                if (existing.size === 0) {
                    listenersRef.current.delete(event);
                }
            } else {
                listenersRef.current.delete(event);
            }
        }
        if (socket) {
            if (callback) {
                socket.off(event, callback);
            } else {
                socket.off(event);
            }
        }
    }, [socket]);

    const value: WebSocketContextType = {
        socket,
        connected,
        emit,
        on,
        off
    };

    return (
        <WebSocketContext.Provider value={value}>
            {children}
        </WebSocketContext.Provider>
    );
};
